import Tooltip from '@components/tooltip/tooltip';
import { theme } from '@config/theme';
import { useTruncate } from '@hooks/useTruncate';
import { useWindowSize } from '@hooks/useWindowSize';
import { Architecture, LocationOn, VisibilityOutlined } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Radio,
	Typography,
} from '@mui/material';
import classNames from 'classnames';
import React, { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type MenuOption = {
	label: string;
	icon: ReactNode;
	onClick: () => void;
};

type CustomCardProps = {
	image: string;
	title: string;
	location: string;
	size: string;
	menuOptions: MenuOption[];
	menuOpen?: boolean;
	onViewDetails?: () => void;
	selectable?: boolean;
	selected?: boolean;
	onSelect?: (id: string) => void;
	id?: string;
	isDisabled?: boolean;
};

export const CropCard: React.FC<CustomCardProps> = ({
	image,
	title,
	location,
	size,
	menuOptions,
	onViewDetails,
	menuOpen = false,
	selectable = false,
	selected = false,
	onSelect,
	id,
	isDisabled = false,
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const { t } = useTranslation();
	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const { truncate } = useTruncate();
	const handleMenuClose = () => {
		setAnchorEl(null);
	};
	const { width } = useWindowSize();

	const sizeTruncateComputedValue = useMemo(() => {
		return truncate(size, 4);
	}, [truncate, size]);

	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: 2,
				boxShadow: theme.boxShadows.primary,
				display: 'flex',
				maxHeight: '200px',
				flexDirection: 'column',
				position: 'relative',
				overflow: 'visible',
			}}
		>
			{/* Image */}
			<div className="relative">
				{isDisabled && (
					<div className="absolute left-4 -top-4 z-20 h-[74px] w-[74px] flex items-center justify-center rounded-xl bg-white/60" />
				)}
				<CardMedia
					component="img"
					image={image}
					alt={title}
					sx={{
						width: 74,
						height: 74,
						borderRadius: '12px',
						boxShadow: theme.boxShadows.primary,
						position: 'absolute',
						top: -16,
						left: 16,
						zIndex: 1,
					}}
				/>
			</div>

			{/* Title and Menu */}
			<Box
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				className="pl-[107px] pt-[11px] pr-[10px]"
			>
				<Tooltip title={title}>
					<Typography
						variant="subtitle1"
						className={classNames(
							'font-roboto text-custom-base line-clamp-1 font-bold leading-custom-tight text-dark',
							{
								'text-secondary': isDisabled,
							}
						)}
					>
						{title}
					</Typography>
				</Tooltip>
				{selectable && (
					<Radio
						disabled={isDisabled}
						value={id}
						color="primary"
						checked={selected}
						onChange={(e) => onSelect?.(e.target.value)}
						onClick={(e) => e.stopPropagation()}
					/>
				)}
				<IconButton size="small" disabled={isDisabled} onClick={handleMenuOpen}>
					<MoreVertIcon />
				</IconButton>
				<Menu
					anchorEl={anchorEl}
					open={Boolean(anchorEl) || menuOpen}
					onClose={handleMenuClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					{menuOptions.map((option, index) => (
						<MenuItem key={index} onClick={option.onClick}>
							<ListItemIcon>{option.icon}</ListItemIcon>
							{option.label}
						</MenuItem>
					))}
				</Menu>
			</Box>

			{/* Location and Size */}
			<CardContent
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: '8px 16px',
					marginTop: '34px',
					borderTop: `1px solid ${theme.palette.custom.offWhite}`,
					borderBottom: `1px solid ${theme.palette.custom.offWhite}`,
				}}
			>
				<Tooltip title={location}>
					<Box display="flex" alignItems="center">
						<LocationOn
							color="primary"
							sx={{ marginRight: 1, color: isDisabled ? theme.palette.custom.gray : 'primary' }}
						/>
						<Typography
							variant="body2"
							noWrap
							className={classNames(
								'text-secondary font-roboto text-sm !font-normal leading-tighter line-clamp-1',
								{
									'text-secondary': isDisabled,
								}
							)}
						>
							{truncate(location, 8)}
						</Typography>
					</Box>
				</Tooltip>
				<Tooltip title={size}>
					<Box display="flex" alignItems="center">
						<Architecture
							color="primary"
							sx={{ marginRight: 1, color: isDisabled ? theme.palette.custom.gray : 'primary' }}
						/>
						<Typography
							className="text-secondary cursor-pointer font-roboto text-sm !font-normal leading-tighter"
							variant="body2"
						>
							{sizeTruncateComputedValue}
						</Typography>
					</Box>
				</Tooltip>
			</CardContent>

			{/* View Details Button */}
			<Button
				disabled={isDisabled}
				startIcon={
					<VisibilityOutlined
						sx={{
							color: theme.palette.custom.gray,
						}}
					/>
				}
				variant="text"
				onClick={onViewDetails}
				sx={{
					height: '100%',
					width: '100%',
					borderRadius: 0,
					textTransform: 'uppercase',
					alignItems: 'flex-end',
					fontWeight: 'bold',
					color: theme.palette.custom.darkGray,
				}}
			>
				{t('myFarm.viewDetails')}
			</Button>
		</Card>
	);
};
